import { ArrowRightOutlined } from '@ant-design/icons';
import IconRound from 'assets/logos/icon-round.svg?react';
import type { TagProps } from 'components/ui';
import { Tag } from 'components/ui';
import dayjs from 'dayjs';
import { colors } from 'design';
import { useIsFirstLineCaregiver } from 'hooks/useCaregiverMe';
import { getLatestActivityDate } from 'hooks/useConversations';
import useMarkConversationAsRead from 'hooks/useMarkConversationAsRead';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import React from 'react';
import { ChatItem, IChatItemProps } from 'react-chat-elements';
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { ConversationDTO } from 'services/api/models/ConversationDTO';
import { AccessTier } from 'services/api/models/UserDTO';
import { selectCurrentUser } from 'store/users/selectors';
import PatientAccessTierIcon from './PatientAccessTierIcon';

const iconRoundUrl = `data:image/svg+xml,${encodeURIComponent(
  renderToStaticMarkup(<IconRound fill={colors.brand.accent} />),
)}`;

export type ChatsListProps = {
  conversations: ConversationDTO[];
  title: string | null;
  readOnlyConvs: boolean;
  maximumConversations?: number;
};

type ChatListItem = Omit<IChatItemProps, 'title' | 'subtitle'> & {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  patientAccessTier?: AccessTier;
};

const FlexibleChatItem = ChatItem as React.ComponentType<ChatListItem>;

const useStyles = createUseStyles({
  title: {
    paddingLeft: 13,
  },
  noChats: {
    textAlign: 'center',
    whiteSpace: 'pre-line',
    color: colors.text.tertiary,
  },
  chatsListCaregiver: {
    display: 'block',
    overflow: 'auto',
    minHeight: '30%',

    '& .rce-citem-body--bottom-status span': {
      backgroundColor: colors.brand.accent,
      borderRadius: '50%',
    },

    // react-chat-elements ships this timestamp at `#0006`, which is 2.9:1 on
    // white. It has no theme API, so overriding its class is the only lever.
    '& .rce-citem-body--top-time': {
      color: colors.text.tertiary,
    },

    '& .rce-citem-avatar img': {
      backgroundColor: 'transparent',
    },
  },
  chatsListPatient: {
    display: 'block',
    overflow: 'auto',
    maxHeight: 280, // about 4 conversations
    // Not a colour: in a mask, only the alpha channel is read, so `#000` here
    // means "fully opaque" and has nothing to do with the palette.
    '-webkit-mask-image': 'linear-gradient(180deg, #000 80%, transparent)',
    marginBottom: 8,

    '& .rce-citem': {
      backgroundColor: 'transparent',
    },

    '& .rce-citem-body--bottom-status span': {
      backgroundColor: colors.brand.accent,
      borderRadius: '50%',
    },

    '& .rce-citem-body--top-time': {
      color: colors.text.tertiary,
    },

    '& .rce-citem-avatar img': {
      backgroundColor: 'transparent',
    },
  },
  backgroundFillBehindGradient: {
    height: 20,
  },
  conversationTitle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },
  accessTierIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
});

export default function ChatsList(props: ChatsListProps) {
  const { conversations, title, readOnlyConvs, maximumConversations } = props;

  const displayedConversations = maximumConversations ? conversations.slice(0, maximumConversations) : conversations;

  // Hooks
  const styles = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const markConversationAsRead = useMarkConversationAsRead();

  // Selectors
  const { conversationId: mainConversationId } = useSelectConversations();
  const currentUser = useSelector(selectCurrentUser);
  const isFirstLineCaregiver = useIsFirstLineCaregiver();

  async function onChatClicked(conversation: Pick<IChatItemProps, 'id'>) {
    if (!readOnlyConvs) {
      navigate(routes.conversation.route.replace(':conversationId', conversation.id.toString()));

      await markConversationAsRead(conversation.id.toString());
      posthog.capture('chats.conversation_opened');
    } else {
      navigate(
        routes.patientHistoricConversation.route
          .replace(':conversationId', mainConversationId)
          .replace(':historicConversation', conversation.id.toString()),
      );
    }
  }
  const convertConversations = (conversationsList: ConversationDTO[]) => {
    return conversationsList.map(conversation => {
      // If the current user is a caregiver, display the patient avatar or a failover avatar. Otherwise, display the caregiver's avatar
      const recipientAvatar = currentUser?.is_caregiver
        ? conversation?.patient.photo_url || iconRoundUrl || t('chats.noCaregiver.avatar')
        : conversation?.current_owner?.photo_url || iconRoundUrl;

      // If the current user is a caregiver, display the patient name or a failover name. Otherwise, display the caregiver's name
      const recipientName =
        currentUser?.is_caregiver && !readOnlyConvs
          ? conversation?.patient?.display_name || t('chats.noCaregiver.name.forCaregiver')
          : conversation?.current_owner?.display_name || t('chats.noCaregiver.name.forPatient');

      const patientAccessTier =
        currentUser?.is_caregiver && !readOnlyConvs ? conversation.patient?.access_tier : undefined;

      const unreadValue: number = conversation.number_of_unread_messages;

      // Determine the waiting label for "En attente" conversations
      // Only show label for conversations waiting for assignment (owner is NOT current user)
      let statusText: string | undefined;
      let statusTone: TagProps['tone'] | undefined;

      if (currentUser?.is_caregiver && conversation.status && !readOnlyConvs) {
        const { status, current_owner } = conversation;
        // Only show label for "En attente" conversations (not "En redirection")
        // This means the owner is NOT the current user
        if (current_owner?.id !== currentUser.id) {
          if (status.value === 'ASSIGNMENT_TO_GROUP_ASKED' && status.group) {
            // Waiting for a group - show the group name
            statusText = t('chats.caregiverStatus.new');
          } else if (status.value === 'ASSIGNMENT_TO_CAREGIVER_ASKED') {
            // Waiting for a specific caregiver - show "me"
            statusText = t('chats.caregiverStatus.waitingForMe');
            statusTone = 'accent';
          } else if (
            status.value === 'WAITING_FOR_FIRST_RESPONSE' ||
            status.value === 'FIRST_ASSIGNMENT_REJECTED' ||
            status.value === 'FIRST_ASSIGNMENT_TIMED_OUT'
          ) {
            statusText = t('chats.caregiverStatus.new');
          }
        } else {
          // my conversations
          const lastMessage = conversation.last_message;
          if (status.value === 'ASSIGNED_TO_CAREGIVER' && lastMessage?.sender?.id !== currentUser.id) {
            const messageDelay = dayjs().diff(dayjs(getLatestActivityDate(conversation)), 'hours');
            if (messageDelay >= 16) {
              statusText = t('chats.caregiverStatus.inNeedOfAttention');
            }
          }
        }
      }
      const lastMessage = conversation.last_message;
      if (currentUser?.is_caregiver && statusText && lastMessage?.sender?.id !== currentUser?.id) {
        const messageDelay = dayjs().diff(dayjs(getLatestActivityDate(conversation)), 'hours');
        if (messageDelay >= 40) {
          statusTone = 'error';
        } else if (messageDelay >= 16) {
          statusTone = 'warning';
        }
      }
      if (readOnlyConvs) {
        statusText =
          conversation.status?.value !== 'ASSIGNED_TO_CAREGIVER'
            ? t('chats.statuses.' + conversation.status?.value)
            : undefined;
        switch (conversation.status?.value) {
          case 'ASSIGNMENT_TO_GROUP_ASKED':
            statusTone = 'warning';
            break;
          case 'ASSIGNMENT_TO_CAREGIVER_ASKED':
            statusTone = 'warning';
            break;
          case 'WAITING_FOR_FIRST_RESPONSE':
          case 'FIRST_ASSIGNMENT_REJECTED':
          case 'FIRST_ASSIGNMENT_TIMED_OUT':
            statusTone = 'warning';
            break;
          case 'ASSIGNED_TO_CAREGIVER':
            statusTone = 'accent';
            break;
          case 'CLOSED':
            statusTone = 'accent';
            break;
        }
      }

      const wasLastMessageSentByCurrentUser = conversation.last_message?.sender?.id === currentUser?.id;
      const showQuestionnaireTag = isFirstLineCaregiver && conversation.questionnaire_needs_review && !readOnlyConvs;

      return {
        ...conversation,
        avatar: recipientAvatar,
        alt: recipientName,
        title: recipientName,
        patientAccessTier,
        subtitle: (
          <>
            {wasLastMessageSentByCurrentUser && (
              <ArrowRightOutlined style={{ color: colors.text.disabled, marginRight: 4 }} />
              //<SendOutlined style={{ color: colors.text.disabled, marginRight: 4 }} />
            )}
            {showQuestionnaireTag && (
              <Tag tone="warning" style={{ marginRight: 6, fontSize: '0.7rem', padding: '0px 8px' }}>
                {t('medicalProfile.caregiver.needsReviewTag')}
              </Tag>
            )}
            {conversation.last_message?.is_deleted ? (
              <em>{t('chats.deletedLastMessage')}</em>
            ) : (
              conversation.last_message?.message_text || t('chats.noLastMessage')
            )}
          </>
        ),
        date: getLatestActivityDate(conversation) ?? new Date(),
        unread: unreadValue,
        statusText,
        statusTone,
      };
    });
  };

  return (
    <>
      {title && <h3 className={styles.title}>{title}</h3>}

      {displayedConversations.length === 0 ? (
        <p className={styles.noChats}>
          {' '}
          {currentUser?.is_caregiver ? t('chats.list.noChats.forCaregiver') : t('chats.list.noChats.forPatient')}
        </p>
      ) : (
        <div className={currentUser?.is_caregiver ? styles.chatsListCaregiver : styles.chatsListPatient}>
          {convertConversations(displayedConversations).map(c => {
            return (
              <FlexibleChatItem
                key={c.id}
                id={c.id}
                avatar={c.avatar}
                alt={c.alt}
                title={
                  c.patientAccessTier ? (
                    <span className={styles.conversationTitle}>
                      <PatientAccessTierIcon accessTier={c.patientAccessTier} className={styles.accessTierIcon} />
                      {c.title}
                    </span>
                  ) : (
                    c.title
                  )
                }
                subtitle={
                  <>
                    {c.statusText && (
                      <Tag tone={c.statusTone} style={{ marginRight: 6, fontSize: '0.7rem', padding: '0px 8px' }}>
                        {c.statusText}
                      </Tag>
                    )}
                    {c.subtitle}
                  </>
                }
                date={c.date}
                dateString={dayjs(c.date).fromNow()}
                unread={c.unread}
                onClick={() => onChatClicked(c)}
              />
            );
          })}
          {displayedConversations.length >= 4 && <div className={styles.backgroundFillBehindGradient} />}
        </div>
      )}
    </>
  );
}
